/**
 * @file PopulationGroupMultiSelect.js
 * @description A multi-select dropdown component for selecting population groups.
 */

import React, { useState, useEffect } from 'react';
import { SearchableDropdown } from '../../helpers/SearchableDropdown_style_b_v2';
import { fetchPopulationGroups } from '../../../services/populationGroupService';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

/**
 * A multi-select component for population groups.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {Array} props.selectedGroups - The currently selected population groups.
 * @param {Function} props.onGroupsChange - The callback function to handle changes in selected groups.
 * @param {string} [props.label="Population Groups"] - The label for the component.
 * @param {boolean} [props.isRequired=false] - Indicates if the component is required.
 * @param {boolean} props.resetTrigger - The trigger to reset the component.
 * @param {boolean} [props.showSelectedItemsInField=true] - Indicates if the selected items should be displayed in the field.
 * @param {boolean} [props.showSelectionBox=true] - Indicates if the selection box should be displayed.
 * @param {string|null} [props.tenantId=null] - The ID of the tenant.
 * @param {boolean} [props.includeSystemGeneratedGroups=true] - If system generated population groups should be included
 * @returns {JSX.Element} The PopulationGroupMultiSelect component.
 */
const PopulationGroupMultiSelect = ({
    className,
    selectedGroups,
    onGroupsChange,
    label = 'Population Groups',
    isRequired = false,
    resetTrigger,
    showSelectedItemsInField = true,
    showSelectionBox = true,
    tenantId = null,
    showSelectedCount = false,
    includeSystemGeneratedGroups = true,
    skipFetch = false,
}) => {
    const [populationGroups, setPopulationGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(!skipFetch);

    useEffect(() => {
        if (skipFetch) {
            setIsLoading(false);
            return;
        }

        const loadPopulationGroups = async () => {
            try {
                setIsLoading(true);
                const groups = await fetchPopulationGroups(tenantId);

                if (includeSystemGeneratedGroups) setPopulationGroups(groups);
                else {
                    setPopulationGroups(
                        groups.filter(group => !group.isSystemGenerated),
                    );
                }
            } catch (error) {
                console.error('Could not fetch population groups:', error);
                Toast.error(getText('msg_16e752137902'));
            } finally {
                setIsLoading(false);
            }
        };

        loadPopulationGroups();
    }, [skipFetch]);

    const handleGroupsChange = selectedOptions => {
        onGroupsChange(selectedOptions);
    };

    return (
        <div className={`form-group mb-3 ${className}`}>
            <label htmlFor="population-group-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
            </label>
            <SearchableDropdown
                id="population-group-dropdown"
                options={populationGroups}
                optionIDField="id"
                updateOptions={handleGroupsChange}
                savedOptions={selectedGroups}
                savedOptionIDField="id"
                disabled={populationGroups.length === 0}
                isLoading={isLoading}
                resetTrigger={resetTrigger}
                showSelectedItemsInField={showSelectedItemsInField}
                showSelectionBox={showSelectionBox}
                showSelectedCount={showSelectedCount}
                allSelectedLabel={getText('msg_e744dd6b3c48')}
            />
        </div>
    );
};

export default PopulationGroupMultiSelect;
